/* platform/PlatformProfile — Control Center user profile. Route: /platform/profile */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead } from './_ui';
import { getRoleInfo, getPlatformRole } from './rbac.js';
import * as authApi from '../api/auth.api.js';

const getSessionUser = () => {
  if (window.__nx_auth?.user) return window.__nx_auth.user;
  try { return JSON.parse(localStorage.getItem('nx-session-v1')); } catch (_) { return null; }
};

const Field = ({ label, value, mono }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', fontFamily: mono ? 'monospace' : undefined }}>{value || '—'}</span>
  </div>
);

export default function PlatformProfile() {
  const navigate = useNavigate();
  const toast    = useToast();
  const [user, setUser]     = useState(getSessionUser);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);

  const handleSaveProfile = async () => {
    setEditBusy(true);
    try {
      await authApi.updateProfile({ name: editForm.name, phone: editForm.phone });
      setUser(u => ({ ...u, name: editForm.name, phone: editForm.phone }));
      setEditForm(null);
      toast({ tone: 'success', icon: 'Check', title: 'Profile updated' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err?.message });
    } finally { setEditBusy(false); }
  };
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr,  setPwErr]  = useState('');
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.detail?.user) setUser(e.detail.user); };
    window.addEventListener('nx-session-changed', h);
    return () => window.removeEventListener('nx-session-changed', h);
  }, []);

  const platformRole = user?.platformRole || getPlatformRole() || 'SUPER_ADMIN';
  const roleInfo     = getRoleInfo(platformRole);
  const name         = user?.name  || 'Platform Admin';
  const email        = user?.email || '';
  const initials     = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const handlePasswordChange = async () => {
    setPwErr('');
    if (!pwForm.current) { setPwErr('Current password is required.'); return; }
    if (pwForm.next.length < 8) { setPwErr('New password must be at least 8 characters.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwErr('Passwords do not match.'); return; }
    setPwBusy(true);
    try {
      await authApi.changePassword(pwForm.current, pwForm.next);
      toast({ tone: 'success', icon: 'Key', title: 'Password changed', desc: 'Your password has been updated.' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwErr(err?.message || 'Password change failed. Check your current password.');
    } finally { setPwBusy(false); }
  };

  const sessionInfo = (() => {
    try {
      const sess = JSON.parse(localStorage.getItem('nx-session-v1'));
      return sess || {};
    } catch (_) { return {}; }
  })();

  return (
    <div className="nx-cc-page" style={{ maxWidth: 680 }}>
      <button className="nx-cc-back" onClick={() => navigate(-1)}>
        <NexusIcon name="ChevronLeft" size={14} /> Back
      </button>

      <PageHead title="My Profile" sub="Platform administrator account" />

      {/* ── Avatar + Identity ─────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '20px 24px', border: '1px solid var(--nx-border)', borderRadius: 12, background: 'var(--nx-surface)', marginBottom: 20 }}>
        <div style={{
          width: 64, height: 64, borderRadius: 14, flexShrink: 0,
          background: 'linear-gradient(135deg,#4692EC,#125AB0)',
          display: 'grid', placeItems: 'center',
          color: '#fff', fontSize: 22, fontWeight: 800,
        }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nx-text)', lineHeight: 1.2, marginBottom: 4 }}>{name}</div>
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 8 }}>{email}</div>
          <Pill tone={roleInfo.tone}>{roleInfo.label}</Pill>
        </div>
      </div>

      {/* ── Account Details ───────────────────────────────────── */}
      <div style={{ border: '1px solid var(--nx-border)', borderRadius: 12, background: 'var(--nx-surface)', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--nx-border)', background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="User" size={14} style={{ color: 'var(--nx-text-muted)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)', flex: 1 }}>Account Details</span>
          {!editForm && (
            <Btn variant="ghost" size="sm" icon="Pencil" onClick={() => setEditForm({ name, phone: user?.phone || '' })}>Edit</Btn>
          )}
        </div>
        {editForm ? (
          <div style={{ padding: '20px 20px' }}>
            <div className="nx-form" style={{ gap: 12 }}>
              <div className="nx-form-row">
                <label>Full Name</label>
                <input className="nx-input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Display name" />
              </div>
              <div className="nx-form-row">
                <label>Phone</label>
                <input className="nx-input" type="tel" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 9876543210" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setEditForm(null)}>Cancel</Btn>
              <Btn variant="primary" icon="Check" disabled={editBusy || !editForm.name.trim()} onClick={handleSaveProfile}>
                {editBusy ? 'Saving…' : 'Save Profile'}
              </Btn>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 20px' }}>
            <Field label="Full Name"      value={name}         />
            <Field label="Email Address"  value={email}        />
            <Field label="Platform Role"  value={roleInfo.label} />
            <Field label="Last Login"     value={user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} />
          </div>
        )}
      </div>

      {/* ── Session Information ───────────────────────────────── */}
      <div style={{ border: '1px solid var(--nx-border)', borderRadius: 12, background: 'var(--nx-surface)', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--nx-border)', background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Monitor" size={14} style={{ color: 'var(--nx-text-muted)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>Session Information</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 20px' }}>
          <Field label="User ID"        value={user?.id || sessionInfo?.userId} mono />
          <Field label="Active Org"     value={sessionInfo?.activeOrgId     ? '···' + sessionInfo.activeOrgId.slice(-8)      : 'None'} mono />
          <Field label="Active Workspace" value={sessionInfo?.activeWorkspaceId ? '···' + sessionInfo.activeWorkspaceId.slice(-8) : 'None'} mono />
          <Field label="Session Scope"  value={platformRole === 'SUPER_ADMIN' ? 'Platform-wide' : roleInfo.label + ' scope'} />
        </div>
      </div>

      {/* ── Change Password ───────────────────────────────────── */}
      <div style={{ border: '1px solid var(--nx-border)', borderRadius: 12, background: 'var(--nx-surface)', marginBottom: 20, overflow: 'hidden' }}>
        <button
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', background: 'var(--nx-surface-alt)', border: 'none', borderBottom: showPw ? '1px solid var(--nx-border)' : 'none', cursor: 'pointer', textAlign: 'left' }}
          onClick={() => setShowPw(v => !v)}>
          <NexusIcon name="Key" size={14} style={{ color: 'var(--nx-text-muted)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)', flex: 1 }}>Change Password</span>
          <NexusIcon name={showPw ? 'ChevronUp' : 'ChevronDown'} size={14} style={{ color: 'var(--nx-text-muted)' }} />
        </button>
        {showPw && (
          <div style={{ padding: '20px 20px' }}>
            <div className="nx-form" style={{ gap: 12 }}>
              {[
                ['Current Password', 'current',  'Current password'],
                ['New Password',     'next',     'At least 8 characters'],
                ['Confirm New',      'confirm',  'Repeat new password'],
              ].map(([label, field, placeholder]) => (
                <div key={field} className="nx-form-row">
                  <label>{label}</label>
                  <input
                    className="nx-input"
                    type="password"
                    placeholder={placeholder}
                    value={pwForm[field]}
                    onChange={e => setPwForm(f => ({ ...f, [field]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handlePasswordChange(); }}
                  />
                </div>
              ))}
              {pwErr && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--nx-danger)', fontSize: 12, padding: '6px 0' }}>
                  <NexusIcon name="AlertCircle" size={13} />{pwErr}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <Btn variant="ghost" onClick={() => { setShowPw(false); setPwForm({ current: '', next: '', confirm: '' }); setPwErr(''); }}>Cancel</Btn>
                <Btn variant="primary" icon="Key" disabled={pwBusy} onClick={handlePasswordChange}>
                  {pwBusy ? 'Saving…' : 'Update Password'}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── MFA (Coming Soon) ─────────────────────────────────── */}
      <div style={{ border: '1px solid var(--nx-border)', borderRadius: 12, background: 'var(--nx-surface)', overflow: 'hidden', opacity: 0.7 }}>
        <div style={{ padding: '14px 20px', background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Shield" size={14} style={{ color: 'var(--nx-text-muted)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)', flex: 1 }}>Two-Factor Authentication</span>
          <Pill tone="gray">Coming Soon</Pill>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <NexusIcon name="Lock" size={28} style={{ color: 'var(--nx-text-faint, #CBD5E1)' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', marginBottom: 2 }}>MFA not yet configured</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Two-factor authentication (TOTP / Passkey) will be available in an upcoming release.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
